import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { useStore } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Constants from 'expo-constants';
import { MysteryShopperClient, ProfileCompletionBonusSummaryResponse } from '../api/api.g';
import { LoggedState } from '../models/AuthModelsCommon';
import { createQualityWarningSession as createAccountSession } from '../helpers/qualityWarningSession';
import { hasProfileBonusOffer } from '../helpers/profileCompletionBonus';
import { myAccountActions } from '../store/myAccountSlice';
import { StringHelper } from '../helpers/stringHelper';
import type { RootState } from '../store/store';
import { useAppDispatch, useAppSelector } from './store-hook';
import useApi from './api-hook';

interface BonusContext {
  summary?: ProfileCompletionBonusSummaryResponse;
  hasOffer: boolean;
  refreshing: boolean;
  observe(): () => void;
  refresh(): Promise<ProfileCompletionBonusSummaryResponse | undefined>;
}

const Context = createContext<BonusContext>({ hasOffer: false, refreshing: false, refresh: async () => undefined, observe: () => () => {} });
export const useProfileCompletionBonus = () => useContext(Context);

export function ProfileCompletionBonusProvider({ children }: { children: ReactNode }) {
  const api = useApi(MysteryShopperClient);
  const store = useStore<RootState>();
  const dispatch = useAppDispatch();
  const cache = useQueryClient();
  const auth = useAppSelector(state => state.auth);
  const accountVisible = useAppSelector(state => state.myAccount.visible);
  const tracker = useRef(createAccountSession()).current;
  const backend = Constants.expoConfig?.extra?.baseUrl ?? 'default';
  const identity = useCallback(() => {
    const value = store.getState().auth;
    return value.logged === LoggedState.Logged && value.idToken && value.user?.uid ? `${backend}:${value.user.uid}` : undefined;
  }, [backend, store]);
  const [session, setSession] = useState(() => tracker.update(identity()));
  const [foreground, setForeground] = useState(AppState.currentState !== 'background' && AppState.currentState !== 'inactive');
  const previousAward = useRef<string | undefined>(undefined);
  const [viewers, setViewers] = useState(0);

  useEffect(() => {
    const update = () => setSession(tracker.update(identity()));
    const unsubscribe = store.subscribe(update);
    update();
    return unsubscribe;
  }, [identity, store, tracker]);
  useEffect(() => () => {
    if (session) {
      void cache.cancelQueries({ queryKey: ['profileCompletionBonus', session.key] });
      cache.removeQueries({ queryKey: ['profileCompletionBonus', session.key] });
    }
  }, [session, cache]);
  useEffect(() => {
    const update = () => setForeground(AppState.currentState !== 'background' && AppState.currentState !== 'inactive' &&
      !(Platform.OS === 'web' && typeof document !== 'undefined' && document.visibilityState === 'hidden'));
    const subscription = AppState.addEventListener('change', update);
    if (Platform.OS === 'web' && typeof document !== 'undefined') document.addEventListener('visibilitychange', update);
    update();
    return () => {
      subscription.remove();
      if (Platform.OS === 'web' && typeof document !== 'undefined') document.removeEventListener('visibilitychange', update);
    };
  }, []);
  const valid = !!session && identity() === session.account;
  const query = useQuery({
    queryKey: ['profileCompletionBonus', session?.key],
    enabled: valid && !!auth.idToken && foreground,
    queryFn: async () => (await api.getProfile()).profileCompletionBonus ?? null,
    useErrorBoundary: false,
    retry: 1,
    // Returning to the foreground must recheck even a recently paused offer.
    staleTime: 0,
    refetchInterval: viewers > 0 ? 60_000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  const refresh = useCallback(async () => {
    if (!session || identity() !== session.account) return undefined;
    const key = session.key;
    const response = await query.refetch();
    return tracker.update(identity())?.key === key && !response.isError ? response.data ?? undefined : undefined;
  }, [session, identity, tracker, query.refetch]);
  const observe = useCallback(() => {
    if (!session || identity() !== session.account) return () => {};
    setViewers(value => value + 1);
    void query.refetch({ cancelRefetch: false });
    let closed = false;
    return () => {
      if (!closed) { closed = true; setViewers(value => Math.max(0, value - 1)); }
    };
  }, [session, identity, query.refetch]);
  useEffect(() => {
    if (accountVisible && valid) void refresh();
  }, [accountVisible, session?.key, valid, refresh]);
  const summary = valid && !query.isError ? query.data ?? undefined : undefined;
  useEffect(() => {
    const award = summary?.awardedAtUtc ? `${session?.key}:${summary.awardedAtUtc}` : undefined;
    if (award && previousAward.current !== award) dispatch(myAccountActions.setMyRewardsSeed(StringHelper.generateRandomString()));
    previousAward.current = award;
  }, [summary?.awardedAtUtc, session?.key, dispatch]);
  return <Context.Provider value={{ summary, hasOffer: hasProfileBonusOffer(summary), refreshing: query.isFetching, refresh, observe }}>{children}</Context.Provider>;
}
